import { useState, useEffect, useContext } from "react";
import { CancelIcon, ConfirmIcon, HelpIcon, ICON_SIZE } from "./actionIcons";
import ReactDOM from "react-dom";
import { Compass, HelpCircle } from "lucide-react";
import { GlobalContext } from "./GlobalContext";
import { emitter } from "./UtilityFunctions";
import { useIsMobile } from "./hooks/useMediaQuery";
import { isPasskeyPromptOpen } from "./PasskeyPrompt";
import { requestTour } from "./tourRegistry";

/**
 * A one-time offer of the app overview, for someone who has just arrived.
 *
 * The tours have existed for a year and were reachable only by clicking the
 * company logo, so in practice nobody was ever offered one. A flag was even
 * being written for this (`hasSeenGlobalTour`) and read by nothing.
 *
 * Declining does not just close: the whole point of asking is that the user
 * learns where the tours live, so "not now" hands over the location of the
 * help menu rather than silently going away.
 */

//Module-level, the same shape PasskeyPrompt uses: a third prompt (SetupPrompt)
//now queues behind this one, so it needs to be able to ask whether this is up
//and to be told when it is not. Not state — the reader is another component's
//effect, which must not re-render on it.
let promptOpen = false;
export const isWelcomeTourPromptOpen = () => promptOpen;
function WelcomeTourPrompt() {
  const globals = useContext(GlobalContext);
  const isMobile = useIsMobile();
  const userID = globals.userData?.UserID;
  //User-scoped like the passkey prompt and the vehicle drafts: on a shared
  //office machine one person's answer must not silence the offer for the next.
  const seenKey = userID ? `welcomeTourSeen_u${userID}` : null;

  const [visible, setVisible] = useState(false);
  //Declining swaps the card's contents rather than closing it.
  const [declined, setDeclined] = useState(false);

  const firstLogin = globals.userData?.FirstLogin;

  useEffect(() => {
    //Tours target desktop chrome the mobile shell does not render, so there is
    //nothing to offer on a phone.
    if (isMobile || !userID || !seenKey) return;
    //Same rule as PasskeyPrompt: never stack on the forced first-login password
    //change, which cannot be dismissed. That session ends at the password
    //change anyway, so the offer arrives on the next login.
    if (firstLogin) return;
    try {
      if (localStorage.getItem(seenKey) === "true") return;
    } catch {
      return;   //storage disabled: no way to remember an answer, so don't ask
    }

    let timer = null;
    const show = () => {
      //Re-read the answer, don't just trust the check above. This component is
      //now BOTH a listener and an emitter of "prompt:closed" (the setup prompt
      //queues behind it), so closing it delivers the event to its own handler —
      //and without this line the prompt would answer itself back onto the
      //screen 600ms later, forever.
      try {
        if (localStorage.getItem(seenKey) === "true") return;
      } catch {
        return;
      }
      //One prompt at a time. If the passkey offer is up, wait for it to close.
      if (isPasskeyPromptOpen()) return;
      //Let the tab strip paint first — the overview's steps target it.
      clearTimeout(timer);
      timer = setTimeout(() => { promptOpen = true; setVisible(true); }, 600);
    };

    show();
    emitter.on("prompt:closed", show);
    return () => {
      clearTimeout(timer);
      emitter.removeListener("prompt:closed", show);
    };
  }, [isMobile, userID, seenKey, firstLogin]);

  //Both answers are answers — record either one so we never ask again.
  const remember = () => {
    try { if (seenKey) localStorage.setItem(seenKey, "true"); } catch { /* non-fatal */ }
  };

  const accept = () => {
    remember();
    promptOpen = false;
    setVisible(false);
    requestTour("overview");
    //Deliberately no "prompt:closed": the screen is not free, it is about to
    //run a guided tour. Letting the next prompt in here would drop a modal on
    //top of the walkthrough the user just asked for. It gets its turn on the
    //next login instead.
  };

  const close = () => {
    promptOpen = false;
    setVisible(false);
    emitter.emit("prompt:closed", "welcome-tour");
    //Land the eye where the copy just said to look.
    const btn = document.getElementById("help-menu-button");
    if (btn) {
      btn.classList.add("help-hint-pulse");
      setTimeout(() => btn.classList.remove("help-hint-pulse"), 3000);
    }
  };

  const decline = () => {
    remember();
    setDeclined(true);
  };

  if (!visible) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) (declined ? close() : decline()); }}
    >
      <div
        id="welcome-tour-prompt"
        style={{
          background: 'var(--theme-surface)',
          borderRadius: 'var(--theme-radius-lg)',
          width: '400px', maxWidth: '90vw',
          boxShadow: 'var(--theme-shadow-dialog)',
        }}
      >
        {declined ? (
          <>
            <div className="modal-body-padded" style={{ textAlign: 'center' }}>
              <HelpCircle
                className="size-10"
                style={{ margin: '0 auto 12px', color: 'var(--theme-accent)' }}
                aria-hidden="true"
              />
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                It will be waiting
              </h3>
              <p className="hint-t" id="welcome-tour-hint">
                Whenever you want a walkthrough, click the{' '}
                <HelpCircle
                  size={14}
                  aria-label="question mark"
                  style={{ display: 'inline', verticalAlign: '-2px', color: 'var(--theme-accent)' }}
                />{' '}
                at the top of the screen. Most pages have a tour of their own.
              </p>
            </div>
            <div className="modal-shell-footer">
              <button type="button" className="btn-t btn-t-primary" id="welcome-tour-gotit" onClick={close}><ConfirmIcon size={ICON_SIZE} />
                Got it
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body-padded" style={{ textAlign: 'center' }}>
              <Compass
                className="size-10"
                style={{ margin: '0 auto 12px', color: 'var(--theme-accent)' }}
                aria-hidden="true"
              />
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
                Take a quick tour?
              </h3>
              <p className="hint-t">
                A two-minute walk through the main screens — inventory, sales, customers and
                reports — so you know where everything lives.
              </p>
            </div>
            <div className="modal-shell-footer">
              <button type="button" className="btn-t btn-t-secondary" id="welcome-tour-decline" onClick={decline}><CancelIcon size={ICON_SIZE} />
                Not now
              </button>
              <button type="button" className="btn-t btn-t-primary" id="welcome-tour-accept" onClick={accept}><HelpIcon size={ICON_SIZE} />
                Show me around
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default WelcomeTourPrompt;
